import { memo, useContext, useCallback } from 'react';

import Cart from 'assets/icons/plus-cart.svg';
import Message from 'types/Message';
import { ConfigContext } from '@providers/ConfigProvider';
import { ProductContext } from '@providers/ProductProvider';
import { SectionId, sections } from '@pages/Product/Product.consts';
import Container from '@components/atoms/Container';
import Button, { Props as ButtonProps } from '@components/atoms/Button';
import Typography from '@components/atoms/Typography';
import Modal from '@components/Modal';
import DeliveryTime from '@templates/DeliveryTime';
import StickyMenu from '@components/StickyMenu';
import AskForAvailability from '@templates/product/AskForAvailability';
import Amount from '@templates/Amount';
import { FormattedMessage } from 'utils/intl';
import getNameByType from 'utils/getNameByType';
import useBoolean from 'hooks/useBoolean';

import messages from './StickyMenuProduct.messages';
import classes from './StickyMenuProduct.module.scss';

type Props = {
  addToCart: () => void;
};

const labels: Record<SectionId, Message> = {
  pageStart: '',
  description: <FormattedMessage {...messages.description} />,
  specification: <FormattedMessage {...messages.specification} />,
  documentation: <FormattedMessage {...messages.documentation} />,
  variants: <FormattedMessage {...messages.variants} />,
  rating: <FormattedMessage {...messages.rating} />,
  recommendedVariants: <FormattedMessage {...messages.recommendedVariants} />,
};

const StickyMenuProduct = ({ addToCart }: Props): JSX.Element => {
  const { shopId } = useContext(ConfigContext);
  const {
    id: variantId,
    currentSupplier,
    names,
    grossPrice,
    fullPrice,
    fullGrossPrice,
    shipsUntil,
    sectionsVisibility,
    isForSale,
    isPrerelease,
    isEol,
    availability,
    setAnchorIndex,
  } = useContext(ProductContext);
  const [modalShown, { on: openModal, setValue: setModalShown }] = useBoolean(false);

  const productName = getNameByType(names, 'VARIANT_TILE', shopId);
  const showDeliveryTime = !!(shipsUntil && availability);

  const onClick = useCallback((anchorId: SectionId) => {
    setAnchorIndex(sections.indexOf(anchorId));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buttonProps: ButtonProps = {
    color: 'primary',
    icon: <Cart className={classes.buttonIcon} />,
    onClick: addToCart,
  };

  const purchaseMap = [
    {
      component: <></>,
      condition: isEol,
    },
    {
      component: (
        <>
          <div className={classes.priceWrapper}>
            <Amount type="netto" price={fullPrice} size="little" isFaded />
            <Amount type="brutto" price={fullGrossPrice} size="small" isBolded />
            {fullGrossPrice.amount > grossPrice.amount && (
              <Typography variant="caption1" lineHeight="18px">
                <FormattedMessage {...messages.extendedPrice} />
              </Typography>
            )}
          </div>
          <div className={classes.buttonWrapper}>
            <Button className={classes.button} size="md" {...buttonProps} wide>
              <FormattedMessage {...messages.addToCart} />
            </Button>
          </div>
        </>
      ),
      condition: availability && isForSale && !isPrerelease,
    },
    {
      component: (
        <>
          <div className={classes.priceWrapper}>
            <Amount type="netto" price={fullPrice} size="little" isFaded />
            <Amount type="brutto" price={fullGrossPrice} size="small" isBolded />
          </div>
          <Button className={classes.askButton} color="tertiary" size="md" onClick={openModal}>
            <span className={classes.buttonText}>
              <FormattedMessage {...messages.askForAvailability} />
            </span>
          </Button>
        </>
      ),
      condition: isForSale && !isPrerelease,
    },
    {
      component: (
        <Button className={classes.askButton} color="tertiary" size="md" onClick={openModal}>
          <span className={classes.buttonText}>
            <FormattedMessage {...messages.askForProduct} />
          </span>
        </Button>
      ),
      condition: isPrerelease,
    },
  ];

  return (
    <StickyMenu
      name={productName}
      labels={labels}
      sections={sections}
      sectionsVisibility={sectionsVisibility}
      onClick={onClick}
      additionalContent={
        <>
          {showDeliveryTime && (
            <div className={classes.deliveryTime}>
              <Container className={classes.deliveryTimeContainer}>
                <DeliveryTime variant="inline" shipsUntil={shipsUntil} currentSupplier={currentSupplier} />
              </Container>
            </div>
          )}
          <Modal controlledIsOpen={modalShown} onOpenChange={setModalShown} hideWhenClickOutside withMobileClose>
            <AskForAvailability variantId={variantId} productName={productName} price={fullGrossPrice} />
          </Modal>
        </>
      }
      isHide={isEol}
    >
      {purchaseMap.find(({ condition }) => condition)?.component}
    </StickyMenu>
  );
};

export default memo(StickyMenuProduct);
